/**
 * 服務訂閱管理（Sprint 4-pre · SYSTEM_ADMIN）
 * func_code: system_subscription_management（is_mana · migration 078 seed · route path===func_code）
 * 待審核佇列（核准/拒絕）+ 全租戶總覽 + 月結帳單 CSV。
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, List, ListItem, ListItemText, Stack, Switch, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import axios from '../api/axios';
import { useMessage } from '../contexts/MessageContext';
import { usePermission } from '../hooks/usePermission';
import {
  listPending, listOverview, approveSubscription, rejectSubscription, billingCsvUrl,
  listAvailableServices, resolveServiceLabel, availableProviders, listSubProviders,
  addSubProvider, removeSubProvider,
  Subscription, ServiceCode, SubProvider,
} from '../services/subscriptionService';

const FUNC_CODE = 'system_subscription_management';
type Tab = 'pending' | 'overview' | 'billing';

const statusChip = (s: string) => {
  const map: Record<string, 'default' | 'success' | 'error' | 'warning'> = {
    pending: 'warning', approved: 'success', rejected: 'error', suspended: 'default', expired: 'default',
  };
  return <Chip size="small" color={map[s] || 'default'} label={s} />;
};

const SystemSubscriptionDashboardPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const tt = (k: string, d: string) => t(`subscription.${k}`, d);
  const { showSuccess, showApiError, showError } = useMessage();
  const { hasPermission, loading: permLoading } = usePermission();
  const canRead = hasPermission(FUNC_CODE, 'read');
  const canUpdate = hasPermission(FUNC_CODE, 'update');

  const [tab, setTab] = useState<Tab>('pending');
  const [pending, setPending] = useState<Subscription[]>([]);
  const [overview, setOverview] = useState<Subscription[]>([]);
  const [services, setServices] = useState<ServiceCode[]>([]);
  const label = (code: string) => resolveServiceLabel(code, services, i18n.language);
  const [busy, setBusy] = useState<number | null>(null);
  // 4-pre-c · 核准 dialog（provider 多選）
  const [approvalSub, setApprovalSub] = useState<Subscription | null>(null);
  const [availProviders, setAvailProviders] = useState<SubProvider[]>([]);
  const [selectedProviders, setSelectedProviders] = useState<number[]>([]);
  const [approveQuota, setApproveQuota] = useState('100000');
  // 4-pre-c · 管理授權 dialog
  const [grantSub, setGrantSub] = useState<Subscription | null>(null);
  const [grants, setGrants] = useState<SubProvider[]>([]);
  const [grantAvail, setGrantAvail] = useState<SubProvider[]>([]);
  // billing
  const now = new Date();
  const [billOrg, setBillOrg] = useState('');
  const [billYear, setBillYear] = useState(String(now.getFullYear()));
  const [billMonth, setBillMonth] = useState(String(now.getMonth() + 1));

  const load = useCallback(async () => {
    try {
      const [p, o, svcs] = await Promise.all([listPending(), listOverview(), listAvailableServices()]);
      setPending(p); setOverview(o); setServices(svcs);
    } catch (err) { showApiError(err); }
  }, [showApiError]);

  useEffect(() => { if (!permLoading && canRead) load(); }, [permLoading, canRead, load]);

  // 開核准 dialog：載入可授權 provider
  const openApproval = async (sub: Subscription) => {
    setApprovalSub(sub); setSelectedProviders([]); setApproveQuota('100000');
    try { setAvailProviders(await availableProviders(sub.id)); } catch (err) { showApiError(err); }
  };

  const handleConfirmApprove = async () => {
    if (!approvalSub || selectedProviders.length < 1) return;
    const def = parseInt(approveQuota, 10);
    if (Number.isNaN(def) || def < 0) { showError('ERR020006', { field: 'quota', detail: approveQuota }); return; }
    setBusy(approvalSub.id);
    try {
      await approveSubscription(approvalSub.id, selectedProviders, def);
      showSuccess('SYS390015', { name: approvalSub.service_code });
      setApprovalSub(null);
      await load();
    } catch (err) { showApiError(err); } finally { setBusy(null); }
  };

  // 管理授權 dialog
  const openGrants = async (sub: Subscription) => {
    setGrantSub(sub);
    try {
      const [g, a] = await Promise.all([listSubProviders(sub.id), availableProviders(sub.id)]);
      setGrants(g); setGrantAvail(a);
    } catch (err) { showApiError(err); }
  };
  const refreshGrants = async (sub: Subscription) => {
    const [g, a] = await Promise.all([listSubProviders(sub.id), availableProviders(sub.id)]);
    setGrants(g); setGrantAvail(a);
  };
  const handleToggleGrant = async (sub: Subscription, g: SubProvider) => {
    try {
      if (g.is_active_for_tenant) await removeSubProvider(sub.id, g.provider_id);
      else await addSubProvider(sub.id, g.provider_id);
      await refreshGrants(sub);
    } catch (err) { showApiError(err); }
  };
  const handleAddGrant = async (sub: Subscription, providerId: number) => {
    try { await addSubProvider(sub.id, providerId); await refreshGrants(sub); }
    catch (err) { showApiError(err); }
  };

  const handleReject = async (sub: Subscription) => {
    const reason = window.prompt(tt('rejectReason', '拒絕原因？'));
    if (!reason) return;
    setBusy(sub.id);
    try {
      await rejectSubscription(sub.id, reason);
      showSuccess('SYS390003', { name: sub.service_code });
      await load();
    } catch (err) { showApiError(err); } finally { setBusy(null); }
  };

  const handleDownloadBilling = async () => {
    const orgId = parseInt(billOrg, 10);
    if (Number.isNaN(orgId)) { showError('ERR020006', { field: 'org_id', detail: billOrg }); return; }
    try {
      const url = billingCsvUrl(orgId, parseInt(billYear, 10), parseInt(billMonth, 10));
      const resp = await axios.get(url, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(resp.data as Blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `billing-org${orgId}-${billYear}-${String(billMonth).padStart(2, '0')}.csv`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch (err) { showApiError(err); }
  };

  if (!permLoading && !canRead) {
    return <div className="page-container"><div className="error-message">{t('common.noPermission', '無此功能權限')}</div></div>;
  }

  const tabBtn = (id: Tab, label: string) => (
    <Button size="small" variant={tab === id ? 'contained' : 'outlined'} onClick={() => setTab(id)}>{label}</Button>
  );

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" gutterBottom>💳 {tt('title', '服務訂閱管理')}</Typography>
      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        {tabBtn('pending', `${tt('pending', '待審核')} (${pending.length})`)}
        {tabBtn('overview', tt('overview', '全租戶總覽'))}
        {tabBtn('billing', tt('billing', '月結帳單'))}
      </Stack>

      {tab === 'pending' && (
        <Card variant="outlined">
          <Table size="small">
            <TableHead><TableRow>
              <TableCell>{tt('tenant', '租戶')}</TableCell>
              <TableCell>{tt('service', '服務')}</TableCell>
              <TableCell>{tt('requestedAt', '申請時間')}</TableCell>
              <TableCell>{tt('actions', '操作')}</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {pending.map((s) => (
                <TableRow key={s.id}>
                  <TableCell>{s.org_name} (#{s.tenant_org_id})</TableCell>
                  <TableCell>{label(s.service_code)}
                    <Typography variant="caption" color="text.secondary"> ({s.service_code})</Typography>
                  </TableCell>
                  <TableCell>{s.requested_at ? new Date(s.requested_at).toLocaleString() : '—'}</TableCell>
                  <TableCell>
                    <Button size="small" color="success" disabled={!canUpdate || busy === s.id}
                      onClick={() => openApproval(s)}>{tt('approve', '審核核准')}</Button>
                    <Button size="small" color="error" disabled={!canUpdate || busy === s.id}
                      onClick={() => handleReject(s)}>{tt('reject', '拒絕')}</Button>
                  </TableCell>
                </TableRow>
              ))}
              {pending.length === 0 && (
                <TableRow><TableCell colSpan={4}>
                  <Typography variant="body2" color="text.secondary">{tt('noPending', '無待審核項目')}</Typography>
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </Card>
      )}

      {tab === 'overview' && (
        <Card variant="outlined">
          <Table size="small">
            <TableHead><TableRow>
              <TableCell>{tt('tenant', '租戶')}</TableCell>
              <TableCell>{tt('service', '服務')}</TableCell>
              <TableCell>{tt('status', '狀態')}</TableCell>
              <TableCell>{tt('defaultQuota', '預設配額/人')}</TableCell>
              <TableCell>{tt('detail', '詳情')}</TableCell>
              <TableCell>{tt('actions', '操作')}</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {overview.map((s) => (
                <TableRow key={s.id}>
                  <TableCell>{s.org_name} (#{s.tenant_org_id})</TableCell>
                  <TableCell>{label(s.service_code)}
                    <Typography variant="caption" color="text.secondary"> ({s.service_code})</Typography>
                  </TableCell>
                  <TableCell>{statusChip(s.status)}</TableCell>
                  <TableCell>{String((s.quota_config as { default_per_user?: number })?.default_per_user ?? '—')}</TableCell>
                  <TableCell>
                    {s.status === 'rejected' ? (
                      <Typography variant="caption" color="error">
                        {tt('rejectedReason', '拒絕原因')}: {s.rejection_reason || '—'}
                        {s.rejected_at && ` · ${new Date(s.rejected_at).toLocaleString()}`}
                      </Typography>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        {tt('billingStart', '計費起始')}: {s.billing_start || '—'}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {s.status === 'approved' && (
                      <Button size="small" disabled={!canUpdate} onClick={() => openGrants(s)}>
                        {tt('manageGrants', '管理授權')}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* 核准 dialog · provider 多選（≥1 gate）*/}
      <Dialog open={!!approvalSub} onClose={() => setApprovalSub(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{tt('approveTitle', '審核核准')} · {approvalSub && label(approvalSub.service_code)}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {tt('tenant', '租戶')}: {approvalSub?.org_name} (#{approvalSub?.tenant_org_id})
          </Typography>
          <Typography variant="subtitle2" sx={{ mt: 1 }}>{tt('selectProviders', '指定 AI Provider（至少 1）')}</Typography>
          <List dense sx={{ maxHeight: 220, overflow: 'auto', border: '1px solid #eee', borderRadius: 1 }}>
            {availProviders.map((p) => (
              <ListItem key={p.provider_id} dense>
                <Checkbox edge="start" checked={selectedProviders.includes(p.provider_id)}
                  onChange={(e) => setSelectedProviders((prev) =>
                    e.target.checked ? [...prev, p.provider_id] : prev.filter((x) => x !== p.provider_id))} />
                <ListItemText primary={<>{p.provider_name}{' '}
                  <Chip size="small" label={p.key_source === 'platform' ? tt('platform', '平台代管') : tt('byoShared', 'BYO 共享')} />
                </>} secondary={p.provider_code} />
              </ListItem>
            ))}
            {availProviders.length === 0 && (
              <ListItem><ListItemText secondary={tt('noProviders', '無可授權的 provider（請先於 AI Provider 設定）')} /></ListItem>
            )}
          </List>
          <Alert severity="warning" sx={{ mt: 1 }}>{tt('byoWarn', '非共享的 BYO Provider 僅限提供租戶自用，不會出現在此清單')}</Alert>
          <TextField size="small" type="number" fullWidth sx={{ mt: 2 }}
            label={tt('defaultQuota', '每使用者預設月配額 tokens（0=不限）')}
            value={approveQuota} onChange={(e) => setApproveQuota(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApprovalSub(null)}>{t('common.cancel', '取消')}</Button>
          <Button variant="contained" disabled={selectedProviders.length < 1 || busy !== null}
            onClick={handleConfirmApprove}>{tt('approveGrant', '核准並授權')}</Button>
        </DialogActions>
      </Dialog>

      {/* 管理授權 dialog */}
      <Dialog open={!!grantSub} onClose={() => setGrantSub(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{tt('manageGrants', '管理授權')} · {grantSub && label(grantSub.service_code)}</DialogTitle>
        <DialogContent>
          <List dense>
            {grants.map((g) => (
              <ListItem key={g.provider_id}
                secondaryAction={grantSub && (
                  <Switch checked={!!g.is_active_for_tenant} disabled={!canUpdate}
                    onChange={() => handleToggleGrant(grantSub, g)} />
                )}>
                <ListItemText primary={`${g.provider_name} (${g.key_source})`}
                  secondary={`${tt('grantedAt', '授權時間')}: ${g.granted_at ? new Date(g.granted_at).toLocaleString() : '—'}${g.is_owner ? ' · owner' : ''}`} />
              </ListItem>
            ))}
            {grants.length === 0 && <ListItem><ListItemText secondary={tt('noGrants', '尚無授權')} /></ListItem>}
          </List>
          <Typography variant="subtitle2" sx={{ mt: 1 }}>{tt('addGrant', '加授權 Provider')}</Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {grantAvail.filter((a) => !grants.some((g) => g.provider_id === a.provider_id && g.is_active_for_tenant))
              .map((a) => (
                <Button key={a.provider_id} size="small" variant="outlined" disabled={!canUpdate}
                  onClick={() => grantSub && handleAddGrant(grantSub, a.provider_id)}>
                  ➕ {a.provider_name}
                </Button>
              ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setGrantSub(null); load(); }}>{t('common.close', '關閉')}</Button>
        </DialogActions>
      </Dialog>

      {tab === 'billing' && (
        <Card variant="outlined" sx={{ p: 2 }}>
          <Alert severity="info" sx={{ mb: 2 }}>{tt('billingHint', '依租戶產生該月 AI 用量費用 CSV（沿用 billing 引擎）')}</Alert>
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField size="small" label={tt('orgId', '租戶 ID')} value={billOrg} onChange={(e) => setBillOrg(e.target.value)} sx={{ width: 100 }} />
            <TextField size="small" label={tt('year', '年')} value={billYear} onChange={(e) => setBillYear(e.target.value)} sx={{ width: 90 }} />
            <TextField size="small" label={tt('month', '月')} value={billMonth} onChange={(e) => setBillMonth(e.target.value)} sx={{ width: 70 }} />
            <Button variant="contained" onClick={handleDownloadBilling}>⬇ {tt('exportCsv', '匯出 CSV')}</Button>
          </Stack>
        </Card>
      )}
    </Box>
  );
};

export default SystemSubscriptionDashboardPage;
